import React, { useState, useMemo } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Label,
} from 'recharts';
import { Variable, DataPoint } from '../api/client';

const TIME_KEY = '__time__';

interface AxisOption {
  id: string;
  name: string;
}

interface Props {
  dependentVariables: Variable[];
  independentVariables: Variable[];
  dataPoints: DataPoint[];
}

const VisualizationView: React.FC<Props> = ({ dependentVariables, independentVariables, dataPoints }) => {
  const numericDepVars = dependentVariables.filter(v => v.type === 'numeric');
  const numericIndepVars = independentVariables.filter(v => v.type === 'numeric');

  const timeOption: AxisOption = { id: TIME_KEY, name: 'Time' };

  const xOptions: AxisOption[] = [timeOption, ...numericIndepVars];
  const yOptions: AxisOption[] = [timeOption, ...numericDepVars];

  const [selectedXId, setSelectedXId] = useState<string>(numericIndepVars[0]?.id ?? TIME_KEY);
  const [selectedYId, setSelectedYId] = useState<string>(numericDepVars[0]?.id ?? TIME_KEY);

  const selectedXName = xOptions.find(o => o.id === selectedXId)?.name ?? '';
  const selectedYName = yOptions.find(o => o.id === selectedYId)?.name ?? '';

  const getValue = (dp: DataPoint, axisId: string): number | null => {
    if (axisId === TIME_KEY) return new Date(dp.created_at).getTime();
    if (dp.values[axisId] === undefined) return null;
    if (dp.values[axisId].value === null) return null;
    const n = Number(dp.values[axisId].value);
    return isNaN(n) ? null : n;
  };

  const scatterData = useMemo(() => {
    return dataPoints
      .map(dp => ({ x: getValue(dp, selectedXId), y: getValue(dp, selectedYId) }))
      .filter((pt): pt is { x: number; y: number } => pt.x !== null && pt.y !== null);
  }, [dataPoints, selectedXId, selectedYId]);

  const formatTick = (axisId: string) => (value: number) =>
    axisId === TIME_KEY ? new Date(value).toLocaleDateString() : String(value);

  const formatTooltip = (value: number, axisId: string) =>
    axisId === TIME_KEY ? new Date(value).toLocaleString() : String(value);

  if (xOptions.length === 1 && yOptions.length === 1) {
    // only time available for both axes — need at least one variable
    return (
      <div className="visualization-view">
        <h3>Visualization</h3>
        <p className="empty-state">
          Add at least one numeric variable to visualize data.
        </p>
      </div>
    );
  }

  return (
    <div className="visualization-view">
      <h3>Visualization</h3>

      <div className="viz-controls">
        <div className="viz-select-group">
          <label>X axis</label>
          <select value={selectedXId} onChange={e => setSelectedXId(e.target.value)}>
            {xOptions.filter(o => !(o.id === TIME_KEY && selectedYId === TIME_KEY)).map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
        <div className="viz-select-group">
          <label>Y axis</label>
          <select value={selectedYId} onChange={e => setSelectedYId(e.target.value)}>
            {yOptions.filter(o => !(o.id === TIME_KEY && selectedXId === TIME_KEY)).map(o => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
        </div>
      </div>

      {scatterData.length === 0 ? (
        <p className="empty-state">No data points have values for both selected axes.</p>
      ) : (
        <div className="viz-chart">
          <ResponsiveContainer width="100%" height={420}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 50, left: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--wnl-border)" />
              <XAxis
                dataKey="x"
                type="number"
                domain={['auto', 'auto']}
                name={selectedXName}
                tick={{ fill: 'var(--wnl-text-muted)', fontSize: 12 }}
                tickFormatter={formatTick(selectedXId)}
              >
                <Label
                  value={selectedXName}
                  position="insideBottom"
                  offset={-30}
                  style={{ fill: 'var(--wnl-ink)', fontWeight: 600, fontSize: 13 }}
                />
              </XAxis>
              <YAxis
                dataKey="y"
                type="number"
                domain={['auto', 'auto']}
                name={selectedYName}
                tick={{ fill: 'var(--wnl-text-muted)', fontSize: 12 }}
                tickFormatter={formatTick(selectedYId)}
              >
                <Label
                  value={selectedYName}
                  angle={-90}
                  position="insideLeft"
                  offset={20}
                  style={{ fill: 'var(--wnl-ink)', fontWeight: 600, fontSize: 13 }}
                />
              </YAxis>
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                contentStyle={{ background: 'var(--wnl-bg)', border: '1px solid var(--wnl-border)', borderRadius: 6, fontSize: 13 }}
                labelStyle={{ color: 'var(--wnl-ink)' }}
                itemStyle={{ color: 'var(--wnl-text-muted)' }}
                formatter={(value: any, name: any) => [
                  formatTooltip(Number(value), name === 'x' ? selectedXId : selectedYId),
                  name === 'x' ? selectedXName : selectedYName,
                ]}
              />
              <Scatter data={scatterData} fill="var(--wnl-data)" fillOpacity={0.8} />
            </ScatterChart>
          </ResponsiveContainer>
          <p className="viz-sample-size">
            {scatterData.length} data point{scatterData.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default VisualizationView;
